/**
 * The KANON switch — `.tog`, drawn once and worn by every surface that needs one: the report
 * editor and list (`pages/reports`), the booking settings and the week pattern (`pages/booking`).
 * Promoted out of `pages/reports` on its second consumer, 26 September 2026.
 *
 * ── A CHECKBOX that looks like a switch, unless the press writes ────────────────────────────
 * A switch announces „on / off" and a listener expects the effect to have happened by the time the
 * word arrives. Where the press only STAGES — the report editor (its save replaces the whole item
 * list at once) and the booking week pattern (its „Zapisz" replaces the whole week) — that is
 * false, so the control says `role="checkbox"` („ticked / unticked"). Where the press WRITES at
 * once — the booking settings — it says `role="switch"`. One drawing for one gesture; the surface
 * decides what the press does and names it with `writesAtOnce`.
 *
 * ── A locked switch stays FOCUSABLE and says why ─────────────────────────────────────────────
 * `aria-disabled` rather than the attribute, with the reason as a RENDERED element `aria-describedby`
 * points at — the shape `shared/ui/dnd/DragHandle` settled and this slice's fifth consumer of it. A
 * `disabled` button drops out of the tab order taking its explanation with it, and a `title` alone
 * reaches neither a touch trainer nor a keyboard one who never hovers.
 */

import { useId } from 'react';

import hidden from './visuallyHidden.module.css';

import styles from './Toggle.module.css';

export interface ToggleProps {
  readonly checked: boolean;
  /** The accessible name. The switch draws no word of its own. */
  readonly label: string;
  /**
   * Why this switch cannot be moved, or `null` when it can. A reason draws the canon's `.tog.lock`
   * — a neutral step above the chip ground, never an accent — and refuses the press.
   */
  readonly lockedReason?: string | null;
  readonly onChange: (checked: boolean) => void;
  /** An instruction rendered once for a whole section, when the surface has one. */
  readonly describedBy?: string;
  /** The press is written at once (a switch), rather than staged until a save (a checkbox). */
  readonly writesAtOnce?: boolean;
}

export function Toggle({
  checked,
  label,
  lockedReason = null,
  onChange,
  describedBy,
  writesAtOnce = false,
}: ToggleProps) {
  const reasonId = useId();
  const locked = lockedReason !== null;

  return (
    <button
      type="button"
      role={writesAtOnce ? 'switch' : 'checkbox'}
      aria-checked={checked}
      aria-label={label}
      aria-disabled={locked ? true : undefined}
      aria-describedby={
        [describedBy, locked ? reasonId : null].filter(Boolean).join(' ') || undefined
      }
      title={lockedReason ?? undefined}
      className={[
        String(styles.tog),
        locked ? String(styles.togLock) : checked ? '' : String(styles.togOff),
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={() => {
        // The no-op is what makes the advisory attribute real: `aria-disabled` tells assistive
        // technology the control is refused and stops nothing on its own.
        if (locked) return;
        onChange(!checked);
      }}
    >
      {locked ? (
        <span id={reasonId} className={hidden.srOnly}>
          {lockedReason}
        </span>
      ) : null}
    </button>
  );
}
